import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Download, FileText, BarChart3, MessageSquare, CheckCircle, XCircle, Clock, AlertCircle } from 'lucide-react';
import { Button } from '../ui/Button';
import { useToast } from '../ui/Toast';
import { apiClient } from '../../lib/api/client';
import { LoadingSpinner } from '../ui/LoadingSpinner';

interface ReportDisplayProps {
  reportId: string;
  isOpen: boolean;
  onClose: () => void;
}

export const ReportDisplay: React.FC<ReportDisplayProps> = ({
  reportId,
  isOpen,
  onClose
}) => {
  const [report, setReport] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'sga' | 'audit' | 'transcript'>('overview');
  const { toast } = useToast();

  useEffect(() => {
    if (isOpen && reportId) {
      fetchReport();
    }
  }, [isOpen, reportId]);

  const fetchReport = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiClient.getReport(reportId);
      setReport(data);
    } catch (err: any) {
      console.error('Error fetching report:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to load report');
      toast({
        title: "Error",
        description: "Failed to load report. Please try again.",
        variant: "error",
        duration: 5000
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const modalContent = (
    <div 
      className="fixed inset-0 flex items-center justify-center bg-black/70 backdrop-blur-md p-4"
      style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 99999 }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div 
        className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col"
        style={{ position: 'relative', zIndex: 100000 }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <BarChart3 className="w-6 h-6 text-blue-600" />
              Call Analysis Report
            </h2>
            {report && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {report.call_count} calls • {formatDuration(report.total_duration_seconds || 0)} total duration
              </p>
            )}
          </div>
          <div className="flex items-center gap-3">
            {report && report.status === 'completed' && (
              <Button
                variant="outline"
                onClick={() => {
                  // TODO: Implement PDF export
                  toast({
                    title: "Export",
                    description: "PDF export coming soon!",
                    variant: "info",
                    duration: 3000
                  });
                }}
              >
                <Download className="w-4 h-4 mr-2" />
                Export PDF
              </Button>
            )}
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-600 dark:text-gray-300" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 bg-gray-50 dark:bg-gray-800">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner size="lg" variant="ai" />
            </div>
          )}

          {error && (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Error Loading Report</h3>
                <p className="text-gray-600 dark:text-gray-300 mb-4">{error}</p>
                <Button variant="outline" onClick={fetchReport}>
                  Retry
                </Button>
              </div>
            </div>
          )}

          {report && !loading && (
            <>
              {/* Tabs */}
              <div className="flex items-center gap-2 mb-6 border-b border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => setActiveTab('overview')}
                  className={`px-4 py-2 font-medium transition-colors ${
                    activeTab === 'overview'
                      ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  Overview
                </button>
                {report.results?.sga_report && (
                  <button
                    onClick={() => setActiveTab('sga')}
                    className={`px-4 py-2 font-medium transition-colors ${
                      activeTab === 'sga'
                        ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400'
                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                    }`}
                  >
                    SGA Analysis
                  </button>
                )}
                {report.results?.call_audit_report && (
                  <button
                    onClick={() => setActiveTab('audit')}
                    className={`px-4 py-2 font-medium transition-colors ${
                      activeTab === 'audit'
                        ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400'
                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                    }`}
                  >
                    Call Audit
                  </button>
                )}
                {report.results?.combined_transcript && (
                  <button
                    onClick={() => setActiveTab('transcript')}
                    className={`px-4 py-2 font-medium transition-colors ${
                      activeTab === 'transcript'
                        ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400'
                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                    }`}
                  >
                    Transcript
                  </button>
                )}
              </div>

              {/* Tab Content */}
              <div className="space-y-6">
                {activeTab === 'overview' && (
                  <div className="space-y-6">
                    {/* Summary Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="bg-white dark:bg-gray-900 rounded-xl p-4 border border-gray-200 dark:border-gray-700 shadow-sm">
                        <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">Total Calls</div>
                        <div className="text-2xl font-bold text-gray-900 dark:text-white">{report.call_count}</div>
                      </div>
                      <div className="bg-white dark:bg-gray-900 rounded-xl p-4 border border-gray-200 dark:border-gray-700 shadow-sm">
                        <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">Total Duration</div>
                        <div className="text-2xl font-bold text-gray-900 dark:text-white">
                          {formatDuration(report.total_duration_seconds || 0)}
                        </div>
                      </div>
                      <div className="bg-white dark:bg-gray-900 rounded-xl p-4 border border-gray-200 dark:border-gray-700 shadow-sm">
                        <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">Status</div>
                        <div className="flex items-center gap-2 mt-1">
                          {report.status === 'completed' && (
                            <>
                              <CheckCircle className="w-5 h-5 text-green-500" />
                              <span className="text-lg font-semibold text-green-500">Completed</span>
                            </>
                          )}
                          {report.status === 'processing' && (
                            <>
                              <Clock className="w-5 h-5 text-blue-500 animate-pulse" />
                              <span className="text-lg font-semibold text-blue-500">Processing</span>
                            </>
                          )}
                          {report.status === 'failed' && (
                            <>
                              <XCircle className="w-5 h-5 text-red-500" />
                              <span className="text-lg font-semibold text-red-500">Failed</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* SGA Summary */}
                    {report.results?.sga_report && (
                      <div className="bg-white dark:bg-gray-900 rounded-xl p-6 border border-gray-200 dark:border-gray-700 shadow-sm">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                          <BarChart3 className="w-5 h-5 text-blue-600" />
                          SGA Analysis Summary
                        </h3>
                        <div className="space-y-3">
                          {report.results.sga_report.overall_score && (
                            <div className="flex items-center justify-between p-3 bg-gray-100 dark:bg-gray-800 rounded-lg">
                              <span className="text-gray-600 dark:text-gray-300">Overall Score</span>
                              <span className="text-xl font-bold text-blue-600">
                                {report.results.sga_report.overall_score}%
                              </span>
                            </div>
                          )}
                          {report.results.sga_report.hire_recommendation && (
                            <div className="flex items-center justify-between p-3 bg-gray-100 dark:bg-gray-800 rounded-lg">
                              <span className="text-gray-600 dark:text-gray-300">Recommendation</span>
                              <span className={`font-semibold ${
                                report.results.sga_report.hire_recommendation.toLowerCase() === 'yes'
                                  ? 'text-green-500'
                                  : 'text-amber-500'
                              }`}>
                                {report.results.sga_report.hire_recommendation}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Call Audit Summary */}
                    {report.results?.call_audit_report && (
                      <div className="bg-white dark:bg-gray-900 rounded-xl p-6 border border-gray-200 dark:border-gray-700 shadow-sm">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                          <MessageSquare className="w-5 h-5 text-blue-600" />
                          Call Audit Summary
                        </h3>
                        <div className="space-y-3">
                          {report.results.call_audit_report.quality_score && (
                            <div className="flex items-center justify-between p-3 bg-gray-100 dark:bg-gray-800 rounded-lg">
                              <span className="text-gray-600 dark:text-gray-300">Quality Score</span>
                              <span className="text-xl font-bold text-blue-600">
                                {report.results.call_audit_report.quality_score}%
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'sga' && report.results?.sga_report && (
                  <div className="bg-white dark:bg-gray-900 rounded-xl p-6 border border-gray-200 dark:border-gray-700 shadow-sm">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">SGA Analysis Details</h3>
                    <div className="space-y-4">
                      <pre className="bg-gray-100 dark:bg-gray-800 p-4 rounded-lg overflow-x-auto text-sm text-gray-600 dark:text-gray-300">
                        {JSON.stringify(report.results.sga_report, null, 2)}
                      </pre>
                    </div>
                  </div>
                )}

                {activeTab === 'audit' && report.results?.call_audit_report && (
                  <div className="bg-white dark:bg-gray-900 rounded-xl p-6 border border-gray-200 dark:border-gray-700 shadow-sm">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Call Audit Details</h3>
                    <div className="space-y-4">
                      <pre className="bg-gray-100 dark:bg-gray-800 p-4 rounded-lg overflow-x-auto text-sm text-gray-600 dark:text-gray-300">
                        {JSON.stringify(report.results.call_audit_report, null, 2)}
                      </pre>
                    </div>
                  </div>
                )}

                {activeTab === 'transcript' && report.results?.combined_transcript && (
                  <div className="bg-white dark:bg-gray-900 rounded-xl p-6 border border-gray-200 dark:border-gray-700 shadow-sm">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                      <FileText className="w-5 h-5 text-blue-600" />
                      Combined Transcript
                    </h3>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400 mb-4">
                        <span>Word Count: {report.results.combined_transcript.word_count}</span>
                        <span>Duration: {formatDuration(report.results.combined_transcript.total_duration_seconds || 0)}</span>
                      </div>
                      <div className="bg-gray-100 dark:bg-gray-800 p-4 rounded-lg max-h-96 overflow-y-auto">
                        <p className="text-gray-600 dark:text-gray-300 whitespace-pre-wrap">
                          {report.results.combined_transcript.full_text}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );

  // Render modal using portal to escape any parent stacking context
  return createPortal(modalContent, document.body);
};


